import { create } from "zustand";

/**
 * Mirrors the live state of the preview iframe in the parent app.
 *
 * `route` is the source of truth for which page is rendered: it's set by the
 * picker on click, and updated again when the iframe's injected client script
 * publishes a `preview-state` patch (e.g. after the user clicks an in-page
 * nav link). PagePicker, useWebsiteComposer, and the auto-select-on-new-page
 * hook all read from here.
 *
 * Every route write is tagged with its `routeSource`:
 *   - 'picker' — parent-initiated; the iframe needs to be navigated.
 *   - 'iframe' — iframe-initiated; the iframe is already there, no nav needed.
 *
 * That tag is what WebsitePreview's nav effect keys off of, instead of an
 * echo-guard timing dance. Source tagging is deterministic — there's no
 * window in which parent and iframe can fight over who navigates.
 *
 * Designed to grow — future preview-side state (scroll position, viewport,
 * hovered link, form state, design-mode hover, …) can land as new fields
 * without changing the message-routing protocol.
 */
export interface PreviewState {
  route: string | null;
}

export type RouteSource = "picker" | "iframe" | null;

interface PreviewStore extends PreviewState {
  routeSource: RouteSource;
  setRoute: (route: string | null) => void;
  applyPatch: (patch: Partial<PreviewState>) => void;
  reset: () => void;
}

const initial: PreviewState & { routeSource: RouteSource } = {
  route: null,
  routeSource: null,
};

export const usePreviewStore = create<PreviewStore>((set) => ({
  ...initial,
  setRoute: (route) => set({ route, routeSource: route === null ? null : "picker" }),
  applyPatch: (patch) => {
    const next: Partial<PreviewState> & { routeSource?: RouteSource } = { ...patch };
    if (typeof patch.route === "string") {
      next.routeSource = "iframe";
    }
    set(next);
  },
  reset: () => set(initial),
}));

export const usePreviewRoute = () => usePreviewStore((s) => s.route);
export const useSetPreviewRoute = () => usePreviewStore((s) => s.setRoute);
